import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Copy } from 'react-feather';
import { pxToRem } from '../../utils/getFontValue';
import { UIColors } from '../../constants/UIConstants';
import { fontWeight } from '../../lib/theme/typography';
import styled from '@emotion/styled';
import ToolTip from '../tooltip/ToolTip';
import { BodyTypography } from '../atoms/typography/body/BodyTypography';
import {
  CenteredFlexBox,
  FlexRightContainer,
} from '../atoms/layout/flexBox/FlexBox';
import {
  QUERY_LOG_UPLOADER_ID,
  queryLogS3TransferConfigureSnippet,
} from '../../constants/commands';
import { useQueryLogCliUploadPanel } from './useQueryLogCliUploadPanel';

const CommandBox = styled(Box)`
  background-color: ${UIColors.neutrals.grey10};
  border: ${pxToRem(1)} solid ${UIColors.neutrals.grey60};
  padding: ${pxToRem(16)};
  margin-bottom: ${pxToRem(16)};
  overflow-y: auto;
`;

const HeaderRow = styled(FlexRightContainer)`
  padding: 0;
  justify-content: space-between;
  margin: ${pxToRem(4)} 0;
`;

const CommandTitle = styled(BodyTypography)`
  line-height: ${pxToRem(38)};
  margin: 0;
`;

const CommandDescription = styled(BodyTypography)`
  line-height: ${pxToRem(32)};
  margin: 0;
`;

const CopyButton = styled(Button)`
  color: ${UIColors.secondary.brightBlue};
  font-weight: ${fontWeight.semiBold};
`;

const CopyIcon = styled(Copy)`
  color: ${UIColors.secondary.brightBlue};
  width: 1.3rem;
  height: 1.3rem;
`;

const CommandText = styled(Box)`
  margin: ${pxToRem(2)} 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: monospace;
  font-size: ${pxToRem(13)};
  line-height: 1.45;
`;

const InlineSpinner = styled(CircularProgress)`
  width: ${pxToRem(30)} !important;
  height: ${pxToRem(30)} !important;
`;

const KeysActions = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${pxToRem(12)};
  margin-bottom: ${pxToRem(12)};
`;

export default function QueryLogCliUploadPanel() {
  const {
    projectId,
    credsBlock,
    credsLoading,
    credsError,
    fetchCredentials,
    s3ConfigDocUrl,
    awsS3CpCommand,
    toolTipTitle,
    handleCopyUploadCommand,
    handleCopyS3ConfigSnippet,
    handleCopyCredentials,
    filesLoading,
    hasDestination,
  } = useQueryLogCliUploadPanel(QUERY_LOG_UPLOADER_ID, '/query-log-details');

  if (!projectId) {
    return null;
  }

  return (
    <Box sx={{ mb: pxToRem(16) }}>
      <HeaderRow>
        <CommandTitle vx="medium_700">
          <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsTitle" />
        </CommandTitle>
        {credsBlock ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: pxToRem(4),
            }}
          >
            <ToolTip direction="top" content={toolTipTitle}>
              <CopyButton
                startIcon={<CopyIcon />}
                variant="text"
                onClick={handleCopyCredentials}
              >
                <FormattedMessage
                  id="steps.misc.commandCopyButton"
                  defaultMessage="Copy to clipboard"
                />
              </CopyButton>
            </ToolTip>
            <CopyButton
              variant="text"
              disabled={credsLoading}
              onClick={() => void fetchCredentials()}
            >
              <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsRefresh" />
            </CopyButton>
            {credsLoading ? <InlineSpinner /> : null}
          </Box>
        ) : null}
      </HeaderRow>
      <CommandDescription vx="medium" sx={{ mb: pxToRem(8) }}>
        <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsDescription" />
      </CommandDescription>
      {credsLoading && !credsBlock ? (
        <KeysActions>
          <InlineSpinner />
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsLoading" />
          </Typography>
        </KeysActions>
      ) : null}
      {credsError ? (
        <Box sx={{ mb: pxToRem(12) }}>
          <Typography variant="body2" color="error" sx={{ mb: pxToRem(8) }}>
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsError" />
          </Typography>
          <CopyButton variant="text" onClick={() => void fetchCredentials()}>
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsTryAgain" />
          </CopyButton>
        </Box>
      ) : null}
      {credsBlock ? (
        <>
          <CommandBox>
            <CommandText>{credsBlock}</CommandText>
          </CommandBox>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: pxToRem(24) }}
          >
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.credentialsExpiryNote" />
          </Typography>
        </>
      ) : null}

      {filesLoading ? (
        <>
          <HeaderRow>
            <CommandTitle vx="medium_700">
              <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadCommandTitle" />
            </CommandTitle>
          </HeaderRow>
          <CommandBox>
            <CenteredFlexBox>
              <InlineSpinner />
            </CenteredFlexBox>
          </CommandBox>
        </>
      ) : null}

      {!filesLoading && !hasDestination ? (
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.unavailable" />
        </Typography>
      ) : null}

      {!filesLoading && hasDestination ? (
        <>
          <HeaderRow>
            <CommandTitle vx="medium_700">
              <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadCommandTitle" />
            </CommandTitle>
            <ToolTip direction="top" content={toolTipTitle}>
              <CopyButton
                startIcon={<CopyIcon />}
                variant="text"
                onClick={handleCopyUploadCommand}
              >
                <FormattedMessage
                  id="steps.misc.commandCopyButton"
                  defaultMessage="Copy to clipboard"
                />
              </CopyButton>
            </ToolTip>
          </HeaderRow>
          <CommandDescription vx="medium">
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadCommandHint" />
          </CommandDescription>
          <CommandBox>
            <CommandText>{awsS3CpCommand}</CommandText>
          </CommandBox>

          <HeaderRow sx={{ mt: pxToRem(8) }}>
            <CommandTitle vx="medium_700">
              <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadS3ConfigTitle" />
            </CommandTitle>
            <ToolTip direction="top" content={toolTipTitle}>
              <CopyButton
                startIcon={<CopyIcon />}
                variant="text"
                onClick={handleCopyS3ConfigSnippet}
              >
                <FormattedMessage
                  id="steps.misc.commandCopyButton"
                  defaultMessage="Copy to clipboard"
                />
              </CopyButton>
            </ToolTip>
          </HeaderRow>
          <Typography variant="body2" sx={{ mb: pxToRem(8), lineHeight: 1.5 }}>
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadS3ConfigDescription" />{' '}
            <Link
              href={s3ConfigDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadS3ConfigDocLink" />
            </Link>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: pxToRem(12),
              lineHeight: 1.5,
              pl: pxToRem(10),
              borderLeft: `${pxToRem(3)} solid`,
              borderColor: 'warning.main',
              color: 'text.primary',
            }}
          >
            <FormattedMessage id="steps.2.b.panel.tabs.cli.destination.uploadS3ConfigMemoryWarning" />
          </Typography>
          <CommandBox>
            <CommandText>{queryLogS3TransferConfigureSnippet}</CommandText>
          </CommandBox>
        </>
      ) : null}
    </Box>
  );
}
